import React, { useEffect, useState } from "react";
import {
  getWrapupCodes,
  getQueueWrapupCodes,
} from "../utilities/genesysCloudUtils";
import "../App.css";
import { Pagination } from "./Pagination";
import { DataTable, DxButton } from "genesys-react-components";
import { Loading } from "./Loading";
import { CatalogTableSurface } from "./CatalogTableSurface";

const PAGE_SIZE = 500;
const MAX_CONCURRENT_QUEUE_REQUESTS = 2;

export function WrapupCodesTable({
  queues,
  queueCache,
  setQueueCache,
  exportStatus,
  onExportCsv,
}) {
  const [wrapupCodes, setWrapupCodes] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchWrapupCodes(), fetchQueueWrapupCodes()]).finally(() => {
      setLoading(false);
    });
  }, [pageNumber, queues]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWrapupCodes = async () => {
    const opts = {
      pageSize: PAGE_SIZE,
      pageNumber,
      sortBy: "name",
      sortOrder: "ascending",
    };
    const data = await getWrapupCodes(opts);
    setWrapupCodes(data.entities);
    setPageCount(data.pageCount);
  };

  const fetchQueueWrapupCodes = async () => {
    const updatedCache = { ...queueCache };
    const queuesToFetch = queues.filter((queue) => !updatedCache[queue.id]);
    const queueOpts = { pageSize: PAGE_SIZE, pageNumber: 1 };

    const fetchQueue = async (queue) => {
      const data = await getQueueWrapupCodes(queue.id, queueOpts);
      updatedCache[queue.id] = data.entities;
    };

    for (let index = 0; index < queuesToFetch.length; index += MAX_CONCURRENT_QUEUE_REQUESTS) {
      const batch = queuesToFetch.slice(index, index + MAX_CONCURRENT_QUEUE_REQUESTS);
      await Promise.all(batch.map(fetchQueue));
    }

    queues.forEach((queue) => {
      queue.wrapUpCodes = updatedCache[queue.id] || [];
    });
    setQueueCache(updatedCache);
  };

  const onPageChange = (pageNumber) => {
    setPageNumber(pageNumber);
    setLoading(true);
  };

  const generateQueueRows = (queues) =>
    queues.map((queue, index) => ({
      cells: [
        {
          content: queue.name,
          align: "center",
          className: `cell-${index}`,
        },
      ],
    }));

  const generateWrapupRows = (wrapupCodes, queues) =>
    wrapupCodes.map((code) => ({
      cells: queues.map((queue, index) => {
        const isActive = queue.wrapUpCodes?.some((queueCode) => queueCode.id === code.id);
        return {
          renderedContent: isActive ? (
            <DxButton type="secondary" className="code-active">
              ✔
            </DxButton>
          ) : (
            " "
          ),
          content: isActive ? "true" : "false",
          align: "center",
          className: `cell-${index}`,
        };
      }),
    }));

  const tableProps = {
    className: "table",
    headerRow: {
      cells: [
        { content: "Wrap Up Code", align: "center" },
        {
          renderedContent: (
            <DataTable
              sortable={false}
              filterable={false}
              rows={generateQueueRows(queues)}
              className="queue-table"
            />
          ),
          content: "",
          align: "left",
          className: "queue-table-cell",
        },
      ],
    },
    indentation: 0,
    rows: wrapupCodes.map((code, index) => ({
      cells: [
        { content: code.name, align: "left" },
        {
          renderedContent: (
            <DataTable
              rows={[generateWrapupRows([code], queues)[0]]}
              className="queue-wrap-code-table"
            />
          ),
          content: "true",
          align: "center",
        },
      ],
    })),
  };

  return (
    <CatalogTableSurface
      exportStatus={exportStatus}
      filteredExportAvailable={true}
      onExportCurrent={() => onExportCsv({
        mode: "current",
        records: wrapupCodes,
        exportContext: { wrapupTableQueues: queues },
      })}
      onExportFiltered={() => onExportCsv({ mode: "all" })}
      rowCount={wrapupCodes.length}
      title="Wrapup Table"
    >
      {loading ? (
        <Loading loadingText="Fetching data..." />
      ) : (
        <>
          <Pagination
            pageCount={pageCount}
            pageNumber={pageNumber}
            onPageChange={onPageChange}
          />
          <DataTable
            sortable={false}
            filterable={false}
            headerRow={tableProps.headerRow}
            rows={tableProps.rows}
            className="main-table table-pin-rows"
          />
        </>
      )}
    </CatalogTableSurface>
  );
}